import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Col, DatePicker, Empty, Row, Spin, Statistic, Table, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getUsage, errorText } from "./api";

/**
 * Settings → Assistant: how many assistant commands each user ran in a month, against the
 * monthly allowance. Owners and admins see everyone; employees only themselves.
 */
const AssistantUsage = () => {
  const { t, i18n } = useTranslation("dashboard");
  const [month, setMonth] = useState(dayjs());
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getUsage(month.format("YYYY-MM"))
      .then(result => alive && (setData(result), setError("")))
      .catch(e => alive && setError(errorText(e, t("Could not load the usage."))))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [month, t]);

  const number = useMemo(() => new Intl.NumberFormat(i18n.language || "de"), [i18n.language]);
  const perUser = data?.limits?.user || 0;

  const columns = [
    { title: t("User"), dataIndex: "name", key: "name", render: v => v || "—" },
    {
      title: t("Commands"),
      dataIndex: "commands",
      key: "commands",
      align: "end",
      sorter: (a, b) => a.commands - b.commands,
      defaultSortOrder: "descend",
      render: v => (perUser ? `${number.format(v)} / ${number.format(perUser)}` : number.format(v))
    },
    {
      title: t("Failed"),
      dataIndex: "errors",
      key: "errors",
      align: "end",
      responsive: ["md"],
      render: v => number.format(v)
    },
    {
      title: t("Tokens"),
      key: "tokens",
      align: "end",
      responsive: ["lg"],
      render: (_, r) => number.format((r.inputTokens || 0) + (r.outputTokens || 0))
    }
  ];

  return (
    <div className="page-content">
      <Typography.Title level={3}>{t("Assistant usage")}</Typography.Title>
      <Typography.Paragraph type="secondary">
        {t("Commands given to the voice and text assistant per user in the selected month.")}
      </Typography.Paragraph>
      <DatePicker
        picker="month"
        allowClear={false}
        value={month}
        onChange={value => value && setMonth(value)}
        disabledDate={d => d.isAfter(dayjs(), "month")}
        style={{ marginBottom: 16 }}
      />
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      <Spin spinning={loading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={8}>
            <Card size="small">
              <Statistic title={t("Commands")} value={data?.totals?.commands || 0} />
            </Card>
          </Col>
          <Col xs={12} md={8}>
            <Card size="small">
              <Statistic title={t("Active users")} value={data?.users?.length || 0} />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small">
              <Statistic
                title={t("Allowance per user and month")}
                value={perUser || t("Unlimited")}
              />
            </Card>
          </Col>
        </Row>
        {data?.users?.length ? (
          <Table
            rowKey="user"
            size="middle"
            columns={columns}
            dataSource={data.users}
            pagination={false}
          />
        ) : (
          !loading && <Empty description={t("The assistant was not used in this month.")} />
        )}
      </Spin>
    </div>
  );
};

export default AssistantUsage;
